import { useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Label } from '@/shared/components/ui/label'
import { DEFAULT_ICON_KEY, ICON_MAP } from '@/admin/components/category-icons'
import { AddressCapture } from '@/admin/components/registration-wizard/address-capture'
import { type ResolvedAddress } from '@/admin/hooks/use-lookups'
import { VehicleLookupField } from '@/admin/components/shared/vehicle-lookup-field'
import {
  buildFormFieldFromMeta,
  useForm,
  type FormField,
  type FormFieldMeta,
} from '@/shared/lib/forms'
import type {
  EntityAttributeDefinition,
  EntityRegistrationConfig,
  EntityRegistrationCustomField,
  EntityRegistrationPreviewField,
  EntityRegistrationPreviewResult,
  EntityRegistrationPreviewValues,
  EntityRegistrationSegment,
  EntityTypeDefinition,
} from '@/admin/hooks/use-entities'
import type { FormContext } from '@/shared/lib/forms'

interface EntityRegistrationFormRendererProps {
  entity: EntityTypeDefinition
  allEntities?: EntityTypeDefinition[]
  registration: EntityRegistrationConfig
  previewResult?: EntityRegistrationPreviewResult | null
  onEvaluate?: (args: {
    step?: string
    values: EntityRegistrationPreviewValues
  }) => Promise<void> | void
}

interface PreviewFieldGroup {
  key: string
  label: string
  icon?: string
  fields: FormFieldMeta[]
}

export function EntityRegistrationFormRenderer({
  entity,
  allEntities = [entity],
  registration,
  previewResult,
  onEvaluate,
}: EntityRegistrationFormRendererProps) {
  const segmentsEnabled = Boolean(registration.segments_enabled)
  const segments = useMemo(
    () =>
      [...(registration.segments ?? [])].sort(
        (a, b) => a.order - b.order || a.label.localeCompare(b.label),
      ),
    [registration.segments],
  )
  const [activeIndex, setActiveIndex] = useState(0)
  const activeSegment = segmentsEnabled ? segments[activeIndex] : undefined
  const fieldGroups = useMemo(
    () =>
      fieldGroupsForRenderer({
        entity,
        allEntities,
        registration,
        segment: activeSegment,
        previewFields: previewResult?.visible_fields ?? [],
      }),
    [activeSegment, allEntities, entity, previewResult?.visible_fields, registration],
  )
  const [values, setValues] = useState<Record<string, unknown>>({})

  const previewValuesFor = useMemo(
    () =>
      (nextValues: Record<string, unknown>): EntityRegistrationPreviewValues => ({
        root: nextValues,
        custom_fields: nextValues,
        entities: relatedEntityValues(entity, registration, nextValues),
      }),
    [entity, registration],
  )

  async function evaluate(step?: string, nextValues = values) {
    await onEvaluate?.({ step, values: previewValuesFor(nextValues) })
  }

  function mergeValues(next: Record<string, unknown>) {
    setValues((current) => ({ ...current, ...next }))
  }

  if (segmentsEnabled && segments.length === 0) {
    return (
      <div className="rounded-[8px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-8 text-center text-[12px] text-[var(--ink-3)]">
        Add at least one segment to preview the multi-step form.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {segmentsEnabled ? (
        <SegmentNav
          segments={segments}
          activeKey={activeSegment?.key}
          onSelect={(segment) =>
            setActiveIndex(Math.max(0, segments.findIndex((item) => item.key === segment.key)))
          }
        />
      ) : null}
      <PreviewStepForm
        key={activeSegment?.key ?? 'single'}
        name={`entity-registration-preview-${entity.key}-${activeSegment?.key ?? 'single'}`}
        title={activeSegment?.label ?? `${entity.label} registration`}
        description={
          activeSegment
            ? activeSegment.members
                .map((member) => segmentMemberEntityKey(entity, member))
                .join(' + ')
            : 'Single form preview'
        }
        fieldGroups={fieldGroups}
        values={values}
        validationHits={previewResult?.validation_hits ?? []}
        onChange={mergeValues}
        onEvaluate={(nextValues) => evaluate(activeSegment?.key, nextValues)}
      />
      {segmentsEnabled ? (
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={activeIndex === 0}
            onClick={() => setActiveIndex((idx) => Math.max(0, idx - 1))}
          >
            Back
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              if (activeIndex >= segments.length - 1) {
                void evaluate(activeSegment?.key)
              } else {
                setActiveIndex((idx) => Math.min(segments.length - 1, idx + 1))
              }
            }}
          >
            {activeIndex >= segments.length - 1 ? 'Run final preview' : 'Continue'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function SegmentNav({
  segments,
  activeKey,
  onSelect,
}: {
  segments: EntityRegistrationSegment[]
  activeKey?: string
  onSelect: (segment: EntityRegistrationSegment) => void
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {segments.map((segment, index) => (
        <Button
          key={segment.key}
          type="button"
          variant={segment.key === activeKey ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => onSelect(segment)}
          className="gap-2"
        >
          <span className="flex size-5 items-center justify-center rounded-full bg-[var(--bg)] text-[10px]">
            {index + 1}
          </span>
          {segment.label}
        </Button>
      ))}
    </div>
  )
}

function PreviewStepForm({
  name,
  title,
  description,
  fieldGroups,
  values,
  validationHits,
  onChange,
  onEvaluate,
}: {
  name: string
  title: string
  description: string
  fieldGroups: PreviewFieldGroup[]
  values: Record<string, unknown>
  validationHits: EntityRegistrationPreviewResult['validation_hits']
  onChange: (next: Record<string, unknown>) => void
  onEvaluate: (nextValues: Record<string, unknown>) => Promise<void> | void
}) {
  const fields = useMemo(
    () => fieldGroups.flatMap((group) => group.fields),
    [fieldGroups],
  )
  const showSplitPanels = fieldGroups.length === 2
  const [submitRequest, setSubmitRequest] = useState(0)
  const pendingSubmissionRef = useRef<{
    remaining: Set<string>
    values: Record<string, unknown>
  } | null>(null)

  function submitPreview() {
    const groupsToSubmit = (
      showSplitPanels ? fieldGroups : [{ key: 'single', label: title, fields }]
    ).filter((group) => group.fields.length > 0)
    pendingSubmissionRef.current = {
      remaining: new Set(groupsToSubmit.map((group) => group.key)),
      values,
    }
    setSubmitRequest((current) => current + 1)
  }

  async function handleGroupSubmit(groupKey: string, rawValues: Record<string, unknown>) {
    const pending = pendingSubmissionRef.current
    if (!pending) {
      const nextValues = { ...values, ...rawValues }
      onChange(nextValues)
      await onEvaluate(nextValues)
      return
    }

    pending.values = { ...pending.values, ...rawValues }
    pending.remaining.delete(groupKey)
    if (pending.remaining.size > 0) return

    const nextValues = pending.values
    pendingSubmissionRef.current = null
    onChange(nextValues)
    await onEvaluate(nextValues)
  }

  const singleGroup = useMemo(
    () => ({ key: 'single', label: title, fields }),
    [fields, title],
  )

  return (
    <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-4">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[14px] font-medium text-[var(--ink)]">{title}</h3>
          <p className="mt-1 text-[11px] text-[var(--ink-3)]">{description}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={submitPreview}>
          Preview step
        </Button>
      </div>
      {fields.length === 0 ? (
        <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] px-3 py-6 text-center text-[12px] text-[var(--ink-3)]">
          No fields are visible for this step.
        </div>
      ) : showSplitPanels ? (
        <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2">
          {fieldGroups.map((group) => (
            <div
              key={group.key}
              className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5"
            >
              <div className="mb-4 flex items-center gap-2">
                <EntityGroupIcon entityKey={group.key} allEntities={fieldGroups} />
                <p className="m-0 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
                  {group.label}
                </p>
              </div>
              {group.fields.length === 0 ? (
                <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] px-3 py-6 text-center text-[12px] text-[var(--ink-3)]">
                  No fields are visible for this entity.
                </div>
              ) : (
                <PreviewGeneratedForm
                  name={`${name}-${group.key}`}
                  fields={group.fields}
                  values={values}
                  submitRequest={submitRequest}
                  onSubmit={(rawValues) => handleGroupSubmit(group.key, rawValues)}
                  className="flex flex-col gap-4"
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <PreviewGeneratedForm
          name={name}
          fields={singleGroup.fields}
          values={values}
          submitRequest={submitRequest}
          onSubmit={(rawValues) => handleGroupSubmit(singleGroup.key, rawValues)}
          className="grid grid-cols-2 gap-4"
        />
      )}
      {validationHits.length > 0 ? (
        <div className="mt-4 flex flex-col gap-2">
          {validationHits.map((hit, index) => (
            <div
              key={`${hit.rule_uuid || hit.name}-${index}`}
              className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <Badge variant={hit.severity === 'BLOCK' ? 'destructive' : 'outline'}>
                  {hit.severity.toLowerCase()}
                </Badge>
                <span className="text-[12px] font-medium text-[var(--ink)]">{hit.name}</span>
              </div>
              <p className="mt-1 text-[11px] text-[var(--ink-3)]">{hit.message}</p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function PreviewGeneratedForm({
  name,
  fields,
  values,
  submitRequest,
  onSubmit,
  className,
}: {
  name: string
  fields: FormFieldMeta[]
  values: Record<string, unknown>
  submitRequest: number
  onSubmit: (rawValues: Record<string, unknown>) => Promise<void> | void
  className: string
}) {
  const formFields = useMemo(
    () =>
      fields.map((field) => buildPreviewFormField(field, fields, values)),
    [fields, values],
  )
  const { Form, ref } = useForm(
    { name, submit: 'footer', fields: formFields },
    async (rawValues) => {
      await onSubmit(rawValues)
    },
    [name, formFields],
  )

  useEffect(() => {
    if (submitRequest === 0) return
    void ref.current?.submitForm()
  }, [ref, submitRequest])

  return <Form className={className} />
}

function buildPreviewFormField(
  field: FormFieldMeta,
  fields: FormFieldMeta[],
  values: Record<string, unknown>,
): FormField {
  const lookupProvider = lookupProviderForField(field)
  if (lookupProvider === 'address') {
    return {
      type: 'custom',
      name: field.code_name,
      label: field.display_name,
      value: values[field.code_name] ?? '',
      className: 'col-span-2',
      render: (formCtx) => (
        <AddressLookupPreviewField field={field} fields={fields} formCtx={formCtx} />
      ),
    }
  }
  if (lookupProvider === 'dvla') {
    return {
      type: 'custom',
      name: field.code_name,
      label: field.display_name,
      value: values[field.code_name] ?? '',
      className: 'col-span-2',
      render: (formCtx) => (
        <VehicleLookupField field={field} fields={fields} formCtx={formCtx} />
      ),
    }
  }
  return buildFormFieldFromMeta(field, values[field.code_name] ?? '', 'submit')
}

function AddressLookupPreviewField({
  field,
  fields,
  formCtx,
}: {
  field: FormFieldMeta
  fields: FormFieldMeta[]
  formCtx: FormContext
}) {
  const addressValue = resolvedAddressFromValues(field, fields, formCtx.values)

  function handleChange(nextAddress: ResolvedAddress | null) {
    const updates = addressValuesForFields(field, fields, nextAddress)
    for (const [codeName, value] of Object.entries(updates)) {
      void formCtx.setFieldValue(codeName, value)
    }
  }

  return (
    <div>
      <Label className="mb-1.5 block clara-label font-normal">
        {field.display_name}
        {field.required ? (
          <span
            aria-hidden="true"
            className="ml-[2px] mt-2 inline-block text-[17px] leading-[4px] text-[var(--status-error)]"
          >
            *
          </span>
        ) : null}
      </Label>
      <AddressCapture
        value={addressValue}
        onChange={handleChange}
        placeholder={field.hint || 'Start typing an address or postcode'}
        searchInputName={`${field.code_name}-preview-address-search`}
        summaryLabel={field.display_name}
      />
      {field.description ? (
        <p className="mt-1 text-[11px] text-[var(--ink-3)]">{field.description}</p>
      ) : null}
    </div>
  )
}

function EntityGroupIcon({
  entityKey,
  allEntities,
}: {
  entityKey: string
  allEntities: PreviewFieldGroup[]
}) {
  const entity = allEntities.find((group) => group.key === entityKey)
  const Icon = ICON_MAP[entity?.icon || DEFAULT_ICON_KEY] ?? ICON_MAP[DEFAULT_ICON_KEY]
  return <Icon size={14} className="text-[var(--ink-3)]" aria-hidden="true" />
}

function fieldGroupsForRenderer({
  entity,
  allEntities,
  registration,
  segment,
  previewFields,
}: {
  entity: EntityTypeDefinition
  allEntities: EntityTypeDefinition[]
  registration: EntityRegistrationConfig
  segment?: EntityRegistrationSegment
  previewFields: EntityRegistrationPreviewField[]
}): PreviewFieldGroup[] {
  const previewByCode = new Map(previewFields.map((field) => [field.code_name, field]))
  const byKey = new Map(allEntities.map((candidate) => [candidate.key, candidate]))
  byKey.set(entity.key, entity)
  const memberKeys = (
    segment?.members
      .map((member) => segmentMemberEntityKey(entity, member))
      .filter((key): key is string => Boolean(key)) ?? []
  )
  const entitiesForStep =
    segment && memberKeys.length > 0
      ? memberKeys.map((key) => byKey.get(key)).filter(isEntityTypeDefinition)
      : [entity]

  const groups = entitiesForStep.map((fieldEntity) => {
    const entityFields = (fieldEntity.attributes ?? [])
      .filter((attribute) => attribute.configuration_data?.value_source === 'registration_field')
      .map((attribute) =>
        entityAttributeToMeta(attribute, previewByCode.get(attribute.code_name), fieldEntity),
      )
    const customFields = (
      (fieldEntity.key === entity.key ? registration : registrationForEntity(fieldEntity))
        .custom_fields ?? []
    ).map((field) => customFieldToMeta(field, previewByCode.get(field.code_name), fieldEntity))
    return {
      key: fieldEntity.key,
      label: fieldEntity.label,
      icon: fieldEntity.icon,
      fields: [...entityFields, ...customFields]
        .filter((field) => !field.hidden)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.display_name.localeCompare(b.display_name)),
    }
  })

  return groups
}

function entityAttributeToMeta(
  attribute: EntityAttributeDefinition,
  preview?: EntityRegistrationPreviewField,
  fieldEntity?: EntityTypeDefinition,
): FormFieldMeta {
  return {
    code_name: attribute.code_name,
    display_name: attribute.display_name,
    field_type: attribute.field_type,
    required: preview?.required ?? attribute.required,
    description: attribute.description,
    hint: attribute.hint,
    options: optionList(attribute.configuration_data?.options),
    order: attribute.order,
    hidden: preview ? !preview.visible : false,
    configuration_data: {
      ...(attribute.configuration_data ?? {}),
      entity_key: fieldEntity?.key,
    },
    source_uuid: attribute.uuid,
  }
}

function customFieldToMeta(
  field: EntityRegistrationCustomField,
  preview?: EntityRegistrationPreviewField,
  fieldEntity?: EntityTypeDefinition,
): FormFieldMeta {
  return {
    code_name: field.code_name,
    display_name: field.display_name,
    field_type: field.field_type,
    required: preview?.required ?? field.required,
    description: field.description,
    options: optionList(field.configuration_data?.options),
    order: field.order,
    hidden: preview ? !preview.visible : false,
    configuration_data: {
      ...(field.configuration_data ?? {}),
      entity_key: fieldEntity?.key,
    },
  }
}

function registrationForEntity(entity: EntityTypeDefinition): EntityRegistrationConfig {
  const registration = entity.metadata?.registration
  return registration && typeof registration === 'object' ? registration : {}
}

function isEntityTypeDefinition(entity: EntityTypeDefinition | undefined): entity is EntityTypeDefinition {
  return Boolean(entity)
}

function segmentMemberEntityKey(
  entity: EntityTypeDefinition,
  member: EntityRegistrationSegment['members'][number],
) {
  if (member.target_entity_key) return member.target_entity_key
  return entity.relationships.find((relationship) => relationship.key === member.relationship_key)?.target_key
}

const ADDRESS_KEY_BY_FIELD_OR_COLUMN: Record<string, keyof ResolvedAddress> = {
  line_1: 'line_1',
  address_line_1: 'line_1',
  addressLine1: 'line_1',
  line_2: 'line_2',
  address_line_2: 'line_2',
  addressLine2: 'line_2',
  town_or_city: 'town_or_city',
  address_town_or_city: 'town_or_city',
  city: 'town_or_city',
  town: 'town_or_city',
  locality: 'town_or_city',
  county: 'county',
  address_county: 'county',
  region: 'county',
  postcode: 'postcode',
  post_code: 'postcode',
  address_postcode: 'postcode',
  postalCode: 'postcode',
  country: 'country',
  address_country: 'country',
  countryCode: 'country',
}

function lookupProviderForField(field: FormFieldMeta) {
  return String(field.configuration_data?.lookup_provider ?? '')
}

function mappedColumnForField(field: FormFieldMeta) {
  return String(field.configuration_data?.maps_to_column ?? '')
}

function resolvedKeyForField(field: FormFieldMeta): keyof ResolvedAddress | undefined {
  return (
    ADDRESS_KEY_BY_FIELD_OR_COLUMN[mappedColumnForField(field)] ??
    ADDRESS_KEY_BY_FIELD_OR_COLUMN[field.code_name]
  )
}

function resolvedToAddressFieldValue(resolved: ResolvedAddress): Record<string, string> {
  return {
    addressLine1: resolved.line_1 ?? '',
    addressLine2: resolved.line_2 ?? '',
    locality: resolved.town_or_city ?? '',
    region: resolved.county ?? '',
    postalCode: resolved.postcode ?? '',
    countryCode: resolved.country ?? '',
  }
}

function addressFieldValueToResolved(value: unknown): Partial<ResolvedAddress> {
  if (!value || typeof value !== 'object') return {}
  const record = value as Record<string, unknown>
  return {
    line_1: String(record.addressLine1 ?? record.line_1 ?? ''),
    line_2: String(record.addressLine2 ?? record.line_2 ?? ''),
    town_or_city: String(record.locality ?? record.town_or_city ?? ''),
    county: String(record.region ?? record.county ?? ''),
    postcode: String(record.postalCode ?? record.postcode ?? ''),
    country: String(record.countryCode ?? record.country ?? ''),
  }
}

function resolvedAddressFromValues(
  primaryField: FormFieldMeta,
  fields: FormFieldMeta[],
  values: Record<string, unknown>,
): ResolvedAddress | null {
  const parts: Partial<ResolvedAddress> = {}
  if (primaryField.field_type === 'ADDRESS') {
    Object.assign(parts, addressFieldValueToResolved(values[primaryField.code_name]))
  }
  for (const field of fields) {
    const resolvedKey = resolvedKeyForField(field)
    if (!resolvedKey) continue
    const raw = values[field.code_name]
    if (raw !== undefined && raw !== null && String(raw).trim().length > 0) {
      parts[resolvedKey] = String(raw)
    }
  }
  if (!Object.values(parts).some((value) => String(value ?? '').trim().length > 0)) {
    return null
  }
  return {
    line_1: parts.line_1 ?? '',
    line_2: parts.line_2 ?? '',
    town_or_city: parts.town_or_city ?? '',
    county: parts.county ?? '',
    postcode: parts.postcode ?? '',
    country: parts.country ?? '',
  }
}

function addressValuesForFields(
  primaryField: FormFieldMeta,
  fields: FormFieldMeta[],
  resolved: ResolvedAddress | null,
) {
  const updates: Record<string, unknown> = {}
  if (primaryField.field_type === 'ADDRESS') {
    updates[primaryField.code_name] = resolved ? resolvedToAddressFieldValue(resolved) : ''
  } else {
    const primaryKey = resolvedKeyForField(primaryField)
    updates[primaryField.code_name] = resolved && primaryKey ? resolved[primaryKey] : ''
  }
  for (const field of fields) {
    const resolvedKey = resolvedKeyForField(field)
    if (!resolvedKey) continue
    updates[field.code_name] = resolved ? resolved[resolvedKey] : ''
  }
  return updates
}

function optionList(raw: unknown) {
  if (!Array.isArray(raw)) return undefined
  return raw
    .map((option) => {
      if (!option || typeof option !== 'object') return null
      const value = String((option as Record<string, unknown>).value ?? '')
      const label = String((option as Record<string, unknown>).label ?? value)
      return value ? { value, label } : null
    })
    .filter((option): option is { value: string; label: string } => option !== null)
}

function relatedEntityValues(
  entity: EntityTypeDefinition,
  registration: EntityRegistrationConfig,
  values: Record<string, unknown>,
) {
  const out: Record<string, Record<string, unknown>> = {}
  for (const segment of registration.segments ?? []) {
    for (const member of segment.members) {
      const key = member.target_entity_key ?? member.relationship_key
      out[key] = values
    }
  }
  out[entity.key] = values
  return out
}
